import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { App, Button, Drawer, Empty, Input, Space, Spin, Tag, Typography } from 'antd';
import { assistantApi, type AssistantMessage } from '../assistant.api';

const bubbleStyle = (role: AssistantMessage['role']): React.CSSProperties => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  background: role === 'user' ? '#4f46e5' : role === 'tool' ? '#f3f4f6' : '#fff',
  color: role === 'user' ? '#fff' : '#111',
  border: role === 'assistant' ? '1px solid #e5e7eb' : 'none',
  borderRadius: 12,
  padding: '8px 12px',
  maxWidth: '85%',
  whiteSpace: 'pre-wrap',
});

export function AssistantDrawer({
  orgId,
  projectId,
  open,
  onClose,
}: {
  orgId: string;
  projectId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { message: toast } = App.useApp();
  const router = useRouter();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [booting, setBooting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || sessionId) return;
    setBooting(true);
    assistantApi
      .createSession(orgId, projectId)
      .then((r) => setSessionId(r.sessionId))
      .catch((e: unknown) =>
        toast.error(e instanceof Error ? e.message : 'Failed to start assistant'),
      )
      .finally(() => setBooting(false));
  }, [open, sessionId, orgId, projectId, toast]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = useMutation({
    mutationFn: (content: string) =>
      assistantApi.sendMessage(orgId, projectId, sessionId ?? '', content),
    onMutate: (content) => {
      const optimistic: AssistantMessage = {
        id: `tmp-${String(Date.now())}`,
        role: 'user',
        content,
        toolName: null,
        data: null,
        pendingAction: null,
        actionStatus: null,
        createdAt: new Date().toISOString(),
      };
      setMessages((m) => [...m, optimistic]);
    },
    onSuccess: (produced) => {
      setMessages((m) => [...m, ...produced]);
      applyNavigation(produced);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to send'),
  });

  const resolve = useMutation({
    mutationFn: ({ messageId, decision }: { messageId: string; decision: 'confirm' | 'reject' }) =>
      assistantApi.resolveAction(orgId, projectId, sessionId ?? '', messageId, decision),
    onSuccess: (produced, { messageId, decision }) => {
      setMessages((m) =>
        m
          .map(
            (msg): AssistantMessage =>
              msg.id === messageId
                ? { ...msg, actionStatus: decision === 'confirm' ? 'confirmed' : 'rejected' }
                : msg,
          )
          .concat(produced),
      );
      applyNavigation(produced);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });

  // The open_page tool answers with data.navigate — perform it client-side so
  // the assistant can literally take the user to the right page/tab.
  const applyNavigation = (produced: AssistantMessage[]) => {
    for (const msg of produced) {
      const nav = (msg.data as { navigate?: { path?: string } } | null)?.navigate;
      if (nav?.path) {
        router.history.push(nav.path);
      }
    }
  };

  const submit = () => {
    const text = draft.trim();
    if (!text || !sessionId || send.isPending) return;
    setDraft('');
    send.mutate(text);
  };

  const visible = messages.filter((m) => m.role !== 'tool' || m.content);

  return (
    <Drawer
      title="AI Assistant"
      placement="right"
      width={440}
      open={open}
      onClose={onClose}
      styles={{ body: { display: 'flex', flexDirection: 'column', padding: 12 } }}
    >
      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {booting ? (
          <Spin />
        ) : visible.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                Ask me to run this project — e.g.
                <br />
                <em>&ldquo;Plan a Ramadan campaign for these products&rdquo;</em>
              </span>
            }
          />
        ) : (
          visible.map((m) => (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={bubbleStyle(m.role)}>
                {m.role === 'tool' ? (
                  <Space size={6}>
                    <Tag color="geekblue" style={{ margin: 0 }}>
                      {m.toolName}
                    </Tag>
                    <span>{m.content}</span>
                  </Space>
                ) : (
                  m.content
                )}
              </div>
              {m.pendingAction && m.actionStatus === 'awaiting_confirmation' ? (
                <Space style={{ marginTop: 6 }}>
                  <Button
                    type="primary"
                    size="small"
                    loading={resolve.isPending}
                    onClick={() => resolve.mutate({ messageId: m.id, decision: 'confirm' })}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="small"
                    onClick={() => resolve.mutate({ messageId: m.id, decision: 'reject' })}
                  >
                    Cancel
                  </Button>
                </Space>
              ) : null}
              {m.actionStatus === 'confirmed' ? (
                <Typography.Text type="success" style={{ fontSize: 12, marginTop: 4 }}>
                  ✓ Confirmed
                </Typography.Text>
              ) : null}
              {m.actionStatus === 'rejected' ? (
                <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Cancelled
                </Typography.Text>
              ) : null}
            </div>
          ))
        )}
        {send.isPending ? <Spin size="small" style={{ alignSelf: 'flex-start' }} /> : null}
      </div>

      <Space.Compact style={{ marginTop: 12 }}>
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask the assistant to do something…"
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={!sessionId}
        />
        <Button type="primary" onClick={submit} loading={send.isPending} disabled={!sessionId}>
          Send
        </Button>
      </Space.Compact>
    </Drawer>
  );
}
